import { useCallback, useEffect, useRef, useState } from 'react'
import WaveSurfer from 'wavesurfer.js'
import Sheet from '../../../components/Sheet'
import Loader from '../../../components/ui/Loader'

/** Потолок отрезка по умолчанию; модель с другим лимитом (Seedance 2.5 — 30 с) передаёт maxSeconds. */
const DEFAULT_MAX_TRIM_DURATION = 15
const MIN_REGION_DURATION = 0.2

function formatTime(s: number): string {
	if (!Number.isFinite(s) || s < 0) return '0.0'
	return s.toFixed(1)
}

function audioBufferToWav(buffer: AudioBuffer): Blob {
	const numChannels = buffer.numberOfChannels
	const sampleRate = buffer.sampleRate
	const format = 1
	const bitDepth = 16
	const bytesPerSample = bitDepth / 8
	const blockAlign = numChannels * bytesPerSample
	const dataLength = buffer.length * blockAlign
	const headerLength = 44
	const totalLength = headerLength + dataLength

	const arrayBuffer = new ArrayBuffer(totalLength)
	const view = new DataView(arrayBuffer)

	const writeString = (offset: number, str: string) => {
		for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i))
	}
	writeString(0, 'RIFF')
	view.setUint32(4, totalLength - 8, true)
	writeString(8, 'WAVE')
	writeString(12, 'fmt ')
	view.setUint32(16, 16, true)
	view.setUint16(20, format, true)
	view.setUint16(22, numChannels, true)
	view.setUint32(24, sampleRate, true)
	view.setUint32(28, sampleRate * blockAlign, true)
	view.setUint16(32, blockAlign, true)
	view.setUint16(34, bitDepth, true)
	writeString(36, 'data')
	view.setUint32(40, dataLength, true)

	let offset = 44
	for (let i = 0; i < buffer.length; i++) {
		for (let ch = 0; ch < numChannels; ch++) {
			const sample = Math.max(-1, Math.min(1, buffer.getChannelData(ch)[i]))
			view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true)
			offset += 2
		}
	}

	return new Blob([arrayBuffer], { type: 'audio/wav' })
}

async function trimAudio(file: File, start: number, end: number): Promise<File> {
	const arrayBuffer = await file.arrayBuffer()
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const Ctx: typeof AudioContext = window.AudioContext || (window as any).webkitAudioContext
	const audioCtx = new Ctx()
	const decoded = await audioCtx.decodeAudioData(arrayBuffer.slice(0))

	const sampleRate = decoded.sampleRate
	const channels = decoded.numberOfChannels
	const startSample = Math.floor(start * sampleRate)
	const endSample = Math.floor(end * sampleRate)
	const length = Math.max(1, endSample - startSample)

	const offlineCtx = new OfflineAudioContext(channels, length, sampleRate)
	const trimmedBuffer = offlineCtx.createBuffer(channels, length, sampleRate)
	for (let ch = 0; ch < channels; ch++) {
		const srcData = decoded.getChannelData(ch)
		const dstData = trimmedBuffer.getChannelData(ch)
		dstData.set(srcData.subarray(startSample, endSample))
	}

	const source = offlineCtx.createBufferSource()
	source.buffer = trimmedBuffer
	source.connect(offlineCtx.destination)
	source.start()

	const rendered = await offlineCtx.startRendering()
	const wavBlob = audioBufferToWav(rendered)
	const trimmedFile = new File(
		[wavBlob],
		file.name.replace(/\.[^.]+$/, '') + '_trimmed.wav',
		{ type: 'audio/wav' },
	)

	await audioCtx.close()
	return trimmedFile
}

interface Props {
	file: File | null
	onApply: (trimmedFile: File, duration: number) => void
	onCancel: () => void
	maxSeconds?: number
}

export default function AudioTrimSheet({ file, onApply, onCancel, maxSeconds }: Props) {
	const MAX_TRIM_DURATION = maxSeconds && maxSeconds > 0 ? maxSeconds : DEFAULT_MAX_TRIM_DURATION
	const open = !!file
	const containerRef = useRef<HTMLDivElement>(null)
	const wavesurferRef = useRef<WaveSurfer | null>(null)
	const objectUrlRef = useRef<string | null>(null)
	const playEndTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const cleanupTimersRef = useRef<(() => void) | null>(null)

	const [ready, setReady] = useState(false)
	const [loadError, setLoadError] = useState<string | null>(null)
	const [isPlaying, setIsPlaying] = useState(false)
	const [duration, setDuration] = useState(0)
	const [regionStart, setRegionStart] = useState(0)
	const [regionEnd, setRegionEnd] = useState(MAX_TRIM_DURATION)
	const [applying, setApplying] = useState(false)

	useEffect(() => {
		if (!open || !file) return

		// queueMicrotask — конвенция репо против set-state-in-effect (каскадные рендеры)
		queueMicrotask(() => {
			setReady(false)
			setLoadError(null)
			setIsPlaying(false)
			setApplying(false)
			setRegionStart(0)
			setRegionEnd(MAX_TRIM_DURATION)
		})

		let cancelled = false

		const initWaveSurfer = () => {
			if (cancelled || !file || !containerRef.current) return

			const url = URL.createObjectURL(file)
			objectUrlRef.current = url

			const isLight = document.documentElement.getAttribute('data-theme') === 'light'

			const ws = WaveSurfer.create({
				container: containerRef.current,
				height: 96,
				waveColor: isLight ? 'rgba(109, 62, 216, 0.35)' : 'rgba(139, 92, 246, 0.4)',
				progressColor: isLight ? 'rgba(109, 62, 216, 0.85)' : 'rgba(139, 92, 246, 0.85)',
				cursorColor: 'rgba(255, 255, 255, 0.6)',
				cursorWidth: 2,
				barWidth: 2,
				barGap: 1,
				barRadius: 2,
				normalize: true,
				interact: false,
				url,
			})
			wavesurferRef.current = ws

			// Волна может не построиться (кодек/браузер: decodeAudioData молчит или
			// падает — владелец 2026-08-23: вечное «Загружаем waveform…», 0.0 сек).
			// Фолбэк: длительность из <audio> — регион и «Применить» работают и без
			// волны; совсем нечитаемый файл — честная ошибка вместо вечного спиннера.
			let settled = false
			const fallbackProbe = (reason: string) => {
				if (cancelled || settled) return
				const probe = new Audio(url)
				const fail = () => {
					if (cancelled || settled) return
					settled = true
					setLoadError('Не удалось прочитать этот аудио-файл. Попробуйте другой формат (mp3 или wav).')
				}
				probe.addEventListener('loadedmetadata', () => {
					if (cancelled || settled) return
					const dur = probe.duration
					if (!isFinite(dur) || dur <= 0) return fail()
					settled = true
					setDuration(dur)
					setRegionStart(0)
					setRegionEnd(Math.min(MAX_TRIM_DURATION, dur))
					setReady(true)
					setLoadError(`Волну построить не удалось (${reason}) — обрезка всё равно работает: выделите отрезок и нажмите «Применить».`)
				})
				probe.addEventListener('error', fail)
			}
			const readyTimeout = setTimeout(() => fallbackProbe('долгое декодирование'), 10000)
			ws.on('ready', () => {
				if (settled) return
				settled = true
				clearTimeout(readyTimeout)
				const dur = ws.getDuration()
				setReady(true)
				setDuration(dur)
				setRegionStart(0)
				setRegionEnd(Math.min(MAX_TRIM_DURATION, dur))
			})
			ws.on('error', () => {
				clearTimeout(readyTimeout)
				fallbackProbe('формат не поддержан браузером')
			})
			cleanupTimersRef.current = () => clearTimeout(readyTimeout)
			ws.on('play', () => setIsPlaying(true))
			ws.on('pause', () => setIsPlaying(false))
			ws.on('finish', () => setIsPlaying(false))
		}

		const rafId = requestAnimationFrame(initWaveSurfer)

		return () => {
			cancelled = true
			cancelAnimationFrame(rafId)
			cleanupTimersRef.current?.()
			cleanupTimersRef.current = null
			if (playEndTimerRef.current) {
				clearInterval(playEndTimerRef.current)
				playEndTimerRef.current = null
			}
			wavesurferRef.current?.destroy()
			wavesurferRef.current = null
			if (objectUrlRef.current) {
				URL.revokeObjectURL(objectUrlRef.current)
				objectUrlRef.current = null
			}
		}
	}, [open, file, MAX_TRIM_DURATION])

	const onStartChange = useCallback(
		(newStart: number) => {
			const s = Math.max(0, Math.min(newStart, duration))
			const e = Math.min(s + MAX_TRIM_DURATION, duration)
			setRegionStart(s)
			setRegionEnd(e)
		},
		[duration, MAX_TRIM_DURATION],
	)

	const onEndChange = useCallback(
		(newEnd: number) => {
			const maxEnd = Math.min(regionStart + MAX_TRIM_DURATION, duration)
			const minEnd = regionStart + MIN_REGION_DURATION
			const e = Math.max(minEnd, Math.min(newEnd, maxEnd))
			setRegionEnd(e)
		},
		[regionStart, duration, MAX_TRIM_DURATION],
	)

	const handlePlayPause = useCallback(() => {
		const ws = wavesurferRef.current
		if (!ws) return
		if (playEndTimerRef.current) {
			clearInterval(playEndTimerRef.current)
			playEndTimerRef.current = null
		}
		ws.playPause()
	}, [])

	const handlePlayRegion = useCallback(() => {
		const ws = wavesurferRef.current
		if (!ws) return
		if (playEndTimerRef.current) {
			clearInterval(playEndTimerRef.current)
			playEndTimerRef.current = null
		}
		ws.setTime(regionStart)
		ws.play()
		playEndTimerRef.current = setInterval(() => {
			const cur = ws.getCurrentTime()
			if (cur >= regionEnd) {
				ws.pause()
				if (playEndTimerRef.current) {
					clearInterval(playEndTimerRef.current)
					playEndTimerRef.current = null
				}
			}
		}, 80)
	}, [regionStart, regionEnd])

	const handleApply = useCallback(async () => {
		if (!file || applying) return
		setApplying(true)
		try {
			const trimmed = await trimAudio(file, regionStart, regionEnd)
			onApply(trimmed, regionEnd - regionStart)
		} catch (err) {
			setLoadError(err instanceof Error ? err.message : 'Ошибка обрезки аудио')
			setApplying(false)
		}
	}, [file, regionStart, regionEnd, applying, onApply])

	const regionDur = Math.max(0, regionEnd - regionStart)
	const canSubmit = ready && !applying && regionDur >= MIN_REGION_DURATION && regionDur <= MAX_TRIM_DURATION

	const startPct = duration > 0 ? (regionStart / duration) * 100 : 0
	const widthPct = duration > 0 ? (regionDur / duration) * 100 : 0

	return (
		<Sheet
			open={open}
			onClose={onCancel}
			variant="center"
			title="Обрезать аудио до 15 секунд"
			className="suno-replace-sheet"
		>
			<div className="suno-replace-sheet__body">
				{loadError && ready && <div className="suno-replace-sheet__error" style={{ marginBottom: 8 }}>{loadError}</div>}
				<div className="suno-replace-sheet__waveform-wrap" style={{ position: 'relative' }}>
					{!ready && !loadError && (
						<div className="suno-replace-sheet__wave-loader">
							<Loader size="md" centered label="Загружаем waveform..." />
						</div>
					)}
					{loadError && !ready && <div className="suno-replace-sheet__error">{loadError}</div>}
					<div ref={containerRef} className="suno-replace-sheet__waveform" />
					{ready && duration > 0 && (
						<div
							style={{
								position: 'absolute',
								top: 0,
								bottom: 0,
								left: `${startPct}%`,
								width: `${widthPct}%`,
								background: 'rgba(139, 92, 246, 0.28)',
								borderLeft: '2px solid rgba(139, 92, 246, 0.85)',
								borderRight: '2px solid rgba(139, 92, 246, 0.85)',
								pointerEvents: 'none',
							}}
						/>
					)}
				</div>

				{ready && duration > 0 && (
					<div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
						<label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
							<span style={{ width: 70, fontSize: 12, color: '#8e97ad' }}>Начало</span>
							<input
								type="range"
								min={0}
								max={duration}
								step={0.1}
								value={regionStart}
								onChange={(e) => onStartChange(parseFloat(e.target.value))}
								style={{ flex: 1, accentColor: 'rgb(139,92,246)' }}
							/>
							<span style={{ width: 50, fontSize: 12, textAlign: 'right' }}>
								{formatTime(regionStart)}с
							</span>
						</label>
						<label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
							<span style={{ width: 70, fontSize: 12, color: '#8e97ad' }}>Конец</span>
							<input
								type="range"
								min={regionStart + MIN_REGION_DURATION}
								max={Math.min(regionStart + MAX_TRIM_DURATION, duration)}
								step={0.1}
								value={regionEnd}
								onChange={(e) => onEndChange(parseFloat(e.target.value))}
								style={{ flex: 1, accentColor: 'rgb(139,92,246)' }}
							/>
							<span style={{ width: 50, fontSize: 12, textAlign: 'right' }}>
								{formatTime(regionEnd)}с
							</span>
						</label>
					</div>
				)}

				<div className="suno-replace-sheet__controls">
					<button
						type="button"
						className="suno-replace-sheet__ctrl-btn"
						onClick={handlePlayPause}
						disabled={!ready}
					>
						{isPlaying ? 'Пауза' : 'Играть всё'}
					</button>
					<button
						type="button"
						className="suno-replace-sheet__ctrl-btn suno-replace-sheet__ctrl-btn--region"
						onClick={handlePlayRegion}
						disabled={!ready}
					>
						Играть выделенное
					</button>
					<div className="suno-replace-sheet__region-info">
						<span className="suno-replace-sheet__region-time">
							Выбрано: {formatTime(regionStart)} – {formatTime(regionEnd)} сек ({formatTime(regionDur)} сек)
						</span>
						<span className="suno-replace-sheet__region-dur">
							всего {formatTime(duration)} сек · максимум {MAX_TRIM_DURATION} сек
						</span>
					</div>
				</div>

				<div className="suno-replace-sheet__footer">
					<button
						type="button"
						className="suno-replace-sheet__cancel"
						onClick={onCancel}
						disabled={applying}
					>
						Отмена
					</button>
					<button
						type="button"
						className="suno-replace-sheet__submit"
						onClick={handleApply}
						disabled={!canSubmit}
					>
						{applying ? 'Обрезаем...' : 'Применить'}
					</button>
				</div>
			</div>
		</Sheet>
	)
}
